import { useContext, useEffect, useMemo, useState } from "react";
import DynamicSkeleton from "../../Common/Skeleton/Skeleton";
import Table from "../../Common/Table/Table";
import ContentDialog from "../../Common/Content-Dialog/ContentDialog";
import Button from "../../Common/Button/Button";
import { ReactComponent as SendIcon } from "../../Assets/Images/send.svg";
import NoDataFoundAnimation from "../../Assets/Json/nodata-found-robot-animation.json";
import AnimationPlayer from "../../Common/Animation-Player/AnimationPlayer";
import styled from "@emotion/styled";
import StageFilterPopup from "../../Common/StageFilterPopup/StageFilterPopup";
import filterDarkIcon from "../../Assets/Images/filter-16-dark.svg";
import filterLightIcon from "../../Assets/Images/filter-16-light.svg";
import "../Settings-Pages/Settings.css";
import {
  VYLABS_BASE_URL,
  LIST_MEMEBERS_OF_TENANT,
  ADD_USER_TO_TENANT,
  UPDATE_MEMBER_ROLE,
  REMOVE_MEMBER,
  CREATE_INVITE_LINK,
  GET_SUBSCRIPTION_DETAILS,
} from "../../helper/url_helper";
import { deleteRequest, get, post } from "../../helper/Api_helper";
import { DynamicContext } from "../../Context/DynamicContext";
import {
  TENANT_CORE_API_KEY,
  TENANT_PRIME_API_KEY,
} from "../../Tenant-Config/TenantConfig";
import { Toast } from "../../Common/Toast-Snackbar/ToastSnackbar";
import AutoComplete from "../../Common/AutoComplete/AutoComplete";
import VybogTALLoaderAnimation from "../../Common/Dynamic-Animations/DynamicAnimations";
import SelectInput from "../../Common/Select-Input/SelectInput";
import { useFormik } from "formik";
import deleteIcon from "../../Assets/Images/delete-active-icon.png";
import updateRoleIcon from "../../Assets/Images/update-role.png";
import { ConfirmationPopup } from "../../Common/PopupStyles/PopupComponent";
import CustomTooltip from "../../Common/Tooltip/Tooltip";
import { useLocation, useNavigate } from "react-router-dom";
import TextInput from "../../Common/TextInput/TextInput";
import copyIcon from "../../Assets/Images/copy-icon.png";
import infoIcon from "../../Assets/Images/infoicon.svg";
import searchIcon from "../../Assets/Images/search-icon.png";
import { CalendarDays } from "lucide-react";

const StyledAnimationContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  marginTop: "-5vw",
  marginBottom: "3vw",
});

const StyledAnimationMessage = styled("h2")({
  color: "#6665EA",
  marginTop: "2vw",
});

const StyledOrSeparator = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "15px 0",
  color: "#675feb",
  fontWeight: "600",
  fontSize: "16px",
  "&::before, &::after": {
    content: '""',
    flex: 1,
    borderBottom: "1px solid #675feb",
    margin: "0 10px",
  },
});

const StyledLinkContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginBottom: "20px",
});

function Members() {
  const {
    userDetails,
    subscriptionDetails,
    setSubscriptionDetails,
    isDarkMode,
    formatDateWithUserTimezone,
  } = useContext(DynamicContext);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRoleModalOpen, setRoleModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [emails, setEmails] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedUserName, setSelectedUserName] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [adminCount, setAdminCount] = useState(0);
  const [isEmailLimitExceeded, setIsEmailLimitExceeded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [pendingFilter, setPendingFilter] = useState("all");
  const [isMemberFilterOpen, setIsMemberFilterOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const updateRole = [
    { title: "Admin", value: "admin" },
    { title: "Member", value: "member" },
    { title: "Manager", value: "manager" },
  ];

  const formik = useFormik({
    initialValues: {
      role: "",
    },
  });

  const maxUsersFeature = subscriptionDetails?.features?.find(
    (f) => f.name === "max_users",
  );
  const userLimit = maxUsersFeature?.limit ?? -1;
  const usedUsers = maxUsersFeature?.used ?? 0;
  const isLimitReached = userLimit !== -1 && usedUsers >= userLimit;
  const showUserLimit = userLimit !== -1;
  const userLimitDisplay = showUserLimit ? `(${usedUsers}/${userLimit})` : "";

  const refetchSubscriptionDetails = async () => {
    try {
      const response = await get(VYLABS_BASE_URL, GET_SUBSCRIPTION_DETAILS);
      setSubscriptionDetails(response);
    } catch (error) {
      console.error("Failed to refetch subscription details:", error);
    }
  };

  const handleEmailChange = (newEmails) => {
    const availableSlots = userLimit - usedUsers;
    if (newEmails.length > availableSlots) {
      setIsEmailLimitExceeded(true);
      Toast(
        "error",
        `You can only add ${availableSlots} more member(s). You have ${availableSlots} slot(s) remaining.`,
      );
      const trimmedEmails = newEmails.slice(0, availableSlots);
      setEmails(trimmedEmails);
    } else {
      setIsEmailLimitExceeded(false);
      setEmails(newEmails);
    }
  };

  const handleAddUserToTenant = async () => {
    const availableSlots = userLimit - usedUsers;
    if (emails.length > availableSlots) {
      Toast("error", `You can only invite ${availableSlots} more member(s).`);
      return;
    }
    if (emails.length === 0) {
      Toast("error", "Please enter email addresses");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter((email) => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      Toast("error", "Please enter valid email addresses");
      return;
    }

    try {
      const requestConfig = {
        headers: {
          "X-API-Key": TENANT_PRIME_API_KEY,
        },
      };
      const response = await post(
        VYLABS_BASE_URL,
        ADD_USER_TO_TENANT,
        {
          emails: emails,
        },
        requestConfig,
      );

      if (response.success) {
        Toast(
          "success",
          `Successfully invited ${response.added_count} member(s)!`,
        );
        await fetchMembers();
        await refetchSubscriptionDetails();
        handleCloseInviteModal();
      } else {
        const errorMessages = response.failed_users
          .map((user) => `${user.email}: ${user.reason}`)
          .join("; ");
        Toast("error", `Failed to invite members: ${errorMessages}`);
        handleCloseInviteModal();
      }
    } catch (err) {
      console.error("Error inviting members:", err);
      Toast("error", "Failed to invite members. Please try again.");
    }
  };

  const handleUpdateMemberRole = async () => {
    if (formik.values.role === "") {
      Toast("error", `Please select "Role" to update the role.`);
    }
    try {
      const requestConfig = {
        headers: {
          "X-API-Key": TENANT_PRIME_API_KEY,
        },
      };

      const response = await post(
        VYLABS_BASE_URL,
        UPDATE_MEMBER_ROLE,
        {
          role: formik.values.role,
          tenantID: userDetails?.tenant_id,
          userID: selectedUserId,
        },
        requestConfig,
      );
      if (response.success) {
        Toast("success", "Role updated successfully");
        handleCloseRoleModal();
        await fetchMembers();
      } else {
        Toast("error", response.message || "Failed to update role");
      }
    } catch (err) {
      console.error("Error updating member role:", err);
      Toast("error", err.response?.data);
    }
  };

  const handleRemoveMember = async () => {
    try {
      const requestConfig = {
        headers: {
          "X-API-Key": TENANT_CORE_API_KEY,
        },
      };
      const response = await deleteRequest(
        VYLABS_BASE_URL,
        REMOVE_MEMBER,
        {
          tenantID: userDetails?.tenant_id,
          userID: selectedUserId,
        },
        requestConfig,
      );
      if (response.success) {
        Toast("success", "User removed successfully");
        setDeleteModalOpen(false);
        await fetchMembers();
        await refetchSubscriptionDetails();
      } else {
        Toast("error", response.message || "Failed to remove member");
      }
    } catch (error) {
      console.error("Error removing member:", error);
      Toast("error", "Failed to remove member. Please try again.");
    }
  };

  const createInviteLink = async () => {
    try {
      const requestConfig = {
        headers: {
          "X-API-Key": TENANT_PRIME_API_KEY,
        },
      };
      const response = await get(
        VYLABS_BASE_URL,
        CREATE_INVITE_LINK,
        requestConfig,
      );
      setInviteLink(response.share_url);
    } catch (error) {
      console.error("Error creating invite link:", error);
      Toast("error", "Failed to generate invite link. Please try again.");
    }
  };

  const handleOpenInviteModal = async () => {
    setIsEmailLimitExceeded(false);
    if (isLimitReached) {
      Toast(
        "error",
        "User limit reached. Upgrade your plan to add more members.",
      );
      return;
    }
    try {
      await createInviteLink();
      setIsModalOpen(true);
      setEmails([]);
    } catch (error) {
      console.error("Error creating invite link:", error);
      Toast("error", "Failed to generate invite link. Please try again.");
    }
  };

  const handleCloseInviteModal = () => {
    setIsModalOpen(false);
    setInviteLink("");
  };

  const handleOpenRoleModal = (userId) => {
    const user = data.find((item) => item.id === userId);
    setSelectedUserId(userId);
    setSelectedUserName(user ? user.name : "");
    setRoleModalOpen(true);
    formik.resetForm();
  };

  const handleOpenMemberDeleteModal = (userId) => {
    const user = data.find((item) => item.id === userId);
    setSelectedUserId(userId);
    setSelectedUserName(user ? user.name : "");
    setDeleteModalOpen(true);
  };

  const handleCloseMemberDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  const handleCloseRoleModal = () => {
    setRoleModalOpen(false);
    setSelectedUserName("");
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    Toast("success", "Invite link copied to clipboard!");
  };

  const fetchMembers = async (filterValue = "") => {
    setIsLoading(true);
    let allUsers = [];
    let offset = 0;
    const limit = 50;
    const trimmedFilter = filterValue.trim();
    const filterQuery = trimmedFilter
      ? `&filter=${encodeURIComponent(trimmedFilter)}`
      : "";
    try {
      while (true) {
        const requestConfig = {
          headers: {
            "X-API-Key": TENANT_CORE_API_KEY,
          },
        };
        const response = await get(
          VYLABS_BASE_URL,
          `${LIST_MEMEBERS_OF_TENANT}?tenant_id=${userDetails?.tenant_id}&offset=${offset}&limit=${limit}${filterQuery}`,
          requestConfig,
        );
        allUsers = allUsers.concat(response.users);
        if (!response.has_more) {
          break;
        }
        offset += limit;
      }
      const uniqueUsers = Array.from(
        new Map(allUsers.map((user) => [user.user.id, user])).values(),
      );
      const adminUsers = uniqueUsers.filter(
        (user) => user.user.role === "ADMIN",
      );
      setAdminCount(adminUsers.length);
      const formattedData = uniqueUsers.map((user, index) => {
        const createdDate = new Date(user.user.created_at);
        const formattedCreatedDate = `${String(createdDate.getDate()).padStart(
          2,
          "0",
        )}-${String(createdDate.getMonth() + 1).padStart(
          2,
          "0",
        )}-${createdDate.getFullYear()}`;
        return {
          id: user.user.id,
          number: index + 1,
          name: `${user.user.first_name} ${user.user.last_name}`,
          email: user.user.email,
          phone: user.user.phone,
          created: formattedCreatedDate,
          created_at: user.user.created_at,
          total_files: user.user.total_files,
          role: user.user.role,
          last_signin: user.user.last_signin || null,
          online: !!user.user.online,
        };
      });
      setData(formattedData);
    } catch (err) {
      console.error("Error fetching members:", err);
      Toast("error", err?.response?.data || "Failed to fetch members. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const handleSearchKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const trimmed = searchTerm.trim();
    setAppliedSearchTerm(trimmed);
    fetchMembers(trimmed);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setAppliedSearchTerm("");
    fetchMembers("");
  };

  useEffect(() => {
    if (location.state?.openAddMemberDialog) {
      setIsModalOpen(true);
      createInviteLink();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const isRecentLastSignin = (lastSignin) => {
    if (!lastSignin) return false;
    const lastSigninDate = new Date(lastSignin);
    if (Number.isNaN(lastSigninDate.getTime())) return false;
    const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
    return Date.now() - lastSigninDate.getTime() <= sevenDaysInMs;
  };

  const getDisplayDate = (dateValue) => {
    const displayDate = new Date(dateValue);
    if (Number.isNaN(displayDate.getTime())) return "-";

    return displayDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const filterCounts = useMemo(
    () => ({
      all: data.length,
      online: data.filter((member) => member.online).length,
      admins: data.filter((member) => member.role === "ADMIN").length,
      managers: data.filter((member) => member.role === "MANAGER").length,
      members: data.filter((member) => member.role === "MEMBER").length,
      inactive: data.filter((member) => !member.online).length,
    }),
    [data],
  );

  const memberFilterOptions = [
    { label: "All", value: "all" },
    { label: "Online", value: "online" },
    { label: "Admins", value: "admins" },
    { label: "Managers", value: "managers" },
    { label: "Members", value: "members" },
    { label: "Inactive", value: "inactive" },
  ];

  const filteredData = useMemo(() => {
    return data.filter((member) => {
      switch (activeFilter) {
        case "online":
          return member.online;
        case "admins":
          return member.role === "ADMIN";
        case "managers":
          return member.role === "MANAGER";
        case "members":
          return member.role === "MEMBER";
        case "inactive":
          return !member.online;
        default:
          return true;
      }
    });
  }, [activeFilter, data]);

  const getRoleStyles = (role) => {
    switch (role) {
      case "ADMIN":
        return {
          backgroundColor: "#ffe9ef",
          borderColor: "#ffb4c5",
          color: "#ff4f78",
        };
      case "MANAGER":
        return {
          backgroundColor: "#eef3ff",
          borderColor: "#aebcff",
          color: "#2941f1",
        };
      case "MEMBER":
        return {
          backgroundColor: "#f1efff",
          borderColor: "#c7c0ff",
          color: "#6860ee",
        };
      case "UNASSOCIATED":
        return {
          backgroundColor: "#fff5e8",
          borderColor: "#ffd29a",
          color: "#e58a00",
        };
      default:
        return {
          backgroundColor: "#f4f5f7",
          borderColor: "#dfe3ea",
          color: "#697386",
        };
    }
  };

  const columns = [
    {
      name: "Name",
      selector: (row) =>
        isLoading ? (
          <DynamicSkeleton variant="text" dimensions={{ width: 100 }} />
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <CustomTooltip title={row.online ? "Online" : "Offline"} arrow>
              <span
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: row.online ? "#2ed573" : "#dcdde1",
                  flexShrink: 0,
                  display: "inline-block",
                }}
              />
            </CustomTooltip>
            <CustomTooltip title={row.name} arrow>
              {row.name}
            </CustomTooltip>
          </div>
        ),
    },
    {
      name: "Email",
      selector: (row) =>
        isLoading ? (
          <DynamicSkeleton variant="text" dimensions={{ width: 100 }} />
        ) : (
          <CustomTooltip title={row.email} arrow>
            {row.email}
          </CustomTooltip>
        ),
    },
    {
      name: "Phone Number",
      selector: (row) =>
        isLoading ? (
          <DynamicSkeleton variant="text" dimensions={{ width: 100 }} />
        ) : (
          row.phone
        ),
    },
    {
      name: "Role",
      selector: (row) =>
        isLoading ? (
          <DynamicSkeleton variant="text" dimensions={{ width: 100 }} />
        ) : (
          <div
            style={{
              ...getRoleStyles(row.role),
              border: "1px solid",
              padding: "3px 7px",
              borderRadius: "12px",
              fontSize: "11px",
              textAlign: "center",
              width: "fit-content",
              fontWeight: "700",
              lineHeight: 1,
            }}
          >
            {row.role}
          </div>
        ),
    },
    {
      name: "Last Online",
      selector: (row) =>
        isLoading ? (
          <DynamicSkeleton variant="text" dimensions={{ width: 100 }} />
        ) : (
          <div>
            {row.online ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  backgroundColor: "#e9fbf2",
                  border: "1px solid #a5ebc3",
                  color: "#23b56a",
                  padding: "4px 9px",
                  borderRadius: "12px",
                  fontSize: "11px",
                  fontWeight: "700",
                  width: "fit-content",
                  lineHeight: 1,
                }}
              >
                <span
                  style={{
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    backgroundColor: "#2ed573",
                    display: "inline-block",
                  }}
                />
                Online
              </div>
            ) : isRecentLastSignin(row.last_signin) ? (
              <CustomTooltip
                title={new Date(row.last_signin).toLocaleString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })}
                arrow
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    backgroundColor: "#f5f5f5",
                    border: "1px solid #d9d9d9",
                    color: "#6b7280",
                    padding: "4px 9px",
                    borderRadius: "12px",
                    fontSize: "11px",
                    fontWeight: "700",
                    lineHeight: 1,
                  }}
                >
                  <span
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      border: "1px solid #8b8b8b",
                      display: "inline-block",
                    }}
                  />
                  Recent
                </span>
              </CustomTooltip>
            ) : row.last_signin ? (
              <CustomTooltip
                title={formatDateWithUserTimezone(row.last_signin)}
                arrow
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    backgroundColor: "#f6f6f6",
                    border: "1px solid #d8dce3",
                    color: "#667085",
                    padding: "4px 9px",
                    borderRadius: "12px",
                    fontSize: "11px",
                    fontWeight: "700",
                    lineHeight: 1,
                  }}
                >
                  <CalendarDays size={12} strokeWidth={2} />
                  {getDisplayDate(row.last_signin)}
                </span>
              </CustomTooltip>
            ) : (
              <CustomTooltip
                title={formatDateWithUserTimezone(row.created_at)}
                arrow
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    backgroundColor: "#f6f6f6",
                    border: "1px solid #d8dce3",
                    color: "#667085",
                    padding: "4px 9px",
                    borderRadius: "12px",
                    fontSize: "11px",
                    fontWeight: "700",
                    lineHeight: 1,
                  }}
                >
                  <CalendarDays size={12} strokeWidth={2} />
                  {getDisplayDate(row.created_at)}
                </span>
              </CustomTooltip>
            )}
          </div>
        ),
    },
    {
      name: "Actions",
      selector: (row) =>
        isLoading ? (
          <DynamicSkeleton variant="text" dimensions={{ width: 100 }} />
        ) : (
          <div
            className="member-table-button-container"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              gap: "10px",
            }}
          >
            {adminCount > 1 || row.role !== "ADMIN" ? (
              <CustomTooltip title={"Update User Role"} arrow>
                <img
                  alt="updateRoleIcon"
                  src={updateRoleIcon}
                  onClick={() => handleOpenRoleModal(row.id)}
                  style={{ width: "25px" }}
                />
              </CustomTooltip>
            ) : (
              <CustomTooltip
                title={"The only admin role cannot be change."}
                arrow
              >
                <img
                  alt="updateRoleIcon"
                  src={updateRoleIcon}
                  style={{
                    width: "25px",
                    opacity: 0.5,
                    cursor: "not-allowed",
                  }}
                />
              </CustomTooltip>
            )}
            {adminCount > 1 || row.role !== "ADMIN" ? (
              <CustomTooltip title={"Delete User"} arrow>
                <img
                  alt="deleteIcon"
                  src={deleteIcon}
                  onClick={() => handleOpenMemberDeleteModal(row.id)}
                  style={{ width: "25px", cursor: "pointer" }}
                />
              </CustomTooltip>
            ) : (
              <CustomTooltip title={"The only admin cannot be deleted."} arrow>
                <img
                  alt="deleteIcon"
                  src={deleteIcon}
                  style={{
                    width: "25px",
                    opacity: 0.5,
                    cursor: "not-allowed",
                  }}
                />
              </CustomTooltip>
            )}
          </div>
        ),
    },
  ];

  return (
    <>
      <div className="upload-resume-header">
        <div className="header-container">
          <div className="member-upper-container">
            <div className="member-title-block">
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <h2>Users</h2>
                {showUserLimit && (
                  <span
                    style={{
                      color: "#666",
                      fontSize: "14px",
                      fontWeight: "500",
                      marginTop: "5px",
                    }}
                  >
                    {userLimitDisplay}
                  </span>
                )}
              </div>
              <p className="member-header-description">
                Manage and control access for all users in your organization
              </p>
            </div>
            <div
              className="member-search-container"
              style={{ display: "flex", gap: "10px", alignItems: "center" }}
            >
              <div className="member-header-search">
                <TextInput
                  placeholder="Search users and hit enter"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  style={{
                    padding: "8px 12px",
                    paddingLeft: "35px",
                    paddingRight: appliedSearchTerm ? "32px" : "12px",
                    border: "1px solid var(--border__color)",
                    borderRadius: "8px",
                    fontSize: "13px",
                    width: "100%",
                    height: "36px",
                    outline: "none",
                    transition: "border-color 0.3s",
                    boxSizing: "border-box",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#6860ee")}
                  onBlur={(e) =>
                    (e.target.style.borderColor = "var(--border__color)")
                  }
                />
                <img
                  src={searchIcon}
                  alt="search"
                  style={{
                    position: "absolute",
                    left: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: "15px",
                    height: "15px",
                    opacity: 0.6,
                    pointerEvents: "none",
                  }}
                />
                {appliedSearchTerm && (
                  <span
                    onClick={handleClearSearch}
                    role="button"
                    aria-label="Clear search"
                    title="Clear search"
                    style={{
                      position: "absolute",
                      right: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      fontSize: "15px",
                      lineHeight: 1,
                      color: "var(--header__color, #666)",
                      opacity: 0.7,
                      userSelect: "none",
                    }}
                  >
                    ✕
                  </span>
                )}
              </div>
              <Button
                label="Add User"
                event={handleOpenInviteModal}
                backgroundColor={isLimitReached ? "#ccc" : "#6860ee"}
                disabled={isLimitReached}
                style={
                  isLimitReached ? { opacity: 0.6, cursor: "not-allowed" } : {}
                }
              />
              <div
                className="filter-stages-and-popup"
                style={{ display: "flex" }}
              >
                <Button
                  iconSrc={isDarkMode ? filterDarkIcon : filterLightIcon}
                  iconPosition="left"
                  iconStyle={{ marginRight: "8px" }}
                  event={() => {
                    setPendingFilter(activeFilter);
                    setIsMemberFilterOpen((prev) => !prev);
                  }}
                  className={`filter-stages-btn ${
                    activeFilter !== "all" ? "is-filtered" : ""
                  }`}
                />
                <StageFilterPopup
                  isOpen={isMemberFilterOpen}
                  subtitle="Create filters to narrow users"
                  onClose={() => setIsMemberFilterOpen(false)}
                  isInitialLoading={isLoading}
                  isDarkMode={isDarkMode}
                  popupClassName="member-stage-filter-popup"
                  customContent={
                    <div className="stage-filter-row">
                      <div className="stage-filter-controls">
                        <div className="filter-label">
                          <img
                            src={isDarkMode ? filterDarkIcon : filterLightIcon}
                            alt="User status"
                            className="filter-label-icon"
                          />
                          <span>User Status</span>
                        </div>
                        <div className="stage-filter-value-wrapper">
                          <select
                            id="member-status-filter"
                            className="member-stage-filter-select"
                            value={pendingFilter}
                            onChange={(event) =>
                              setPendingFilter(event.target.value)
                            }
                          >
                            {memberFilterOptions.map((filter) => (
                              <option key={filter.value} value={filter.value}>
                                {`${filter.label} (${filterCounts[filter.value]})`}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  }
                  customActions={
                    <>
                      <Button
                        event={() => {
                          setPendingFilter(activeFilter);
                          setIsMemberFilterOpen(false);
                        }}
                        label="Clear"
                        aria-label="Cancel user filters"
                        backgroundColor="#a90606"
                        color="#ffffffff"
                        border="#a90606"
                        hoverd={false}
                      />
                      <Button
                        event={() => {
                          setActiveFilter(pendingFilter);
                          setIsMemberFilterOpen(false);
                        }}
                        label="Apply"
                        aria-label="Apply user filters"
                      />
                    </>
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="member-main-resume-container">
        {isLoading ? (
          <div className="loading-animation">
            <VybogTALLoaderAnimation
              animationHeight="110px"
              animationWidth="110px"
            />
          </div>
        ) : filteredData.length > 0 ? (
          <div className="table-container">
            <Table
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
              fixedHeader
              // fixedHeaderScrollHeight="300px"
              backgroundColor="var(--body__background)"
              color="var(--header__color)"
            />
          </div>
        ) : (
          <div className="no-data-message">
            <div className="login-animation">
              <AnimationPlayer
                animationSrc={NoDataFoundAnimation}
                playerHeight="50vh"
                playerWidth="500px"
              />
              <StyledAnimationContainer className="error-div">
                <StyledAnimationMessage className="exist">
                  "No member is in your possession."
                </StyledAnimationMessage>
              </StyledAnimationContainer>
            </div>
          </div>
        )}
      </div>
      <ContentDialog
        isOpen={isModalOpen}
        title="Add User"
        onClose={handleCloseInviteModal}
        onCloseIconClose={handleCloseInviteModal}
        submitButtonLabel="Send Invite"
        cancelButtonLabel="Cancel"
        event={handleAddUserToTenant}
        submitButtonIcon={<SendIcon width={15} height={15} />}
        overflowY={true}
      >
        <div className="invite-member-popup">
          <p style={{ marginBottom: "20px", color: "#675feb" }}>
            Add a new user by entering their email address who already has user
            account.
          </p>
          <div>
            <AutoComplete
              multiple
              placeholder="Enter your team user emails here"
              tags={true}
              name="teamEmails"
              value={emails}
              onChange={handleEmailChange}
              InputWrapperStyle={{
                height: "35px",
                overflowY: "auto",
                padding: "10px",
                marginBottom: "30px",
                marginTop: "15px",
                border: isEmailLimitExceeded
                  ? "1px solid #ff4444"
                  : "1px solid #ccc",
              }}
              disabled={isLimitReached}
            />
          </div>
          <StyledOrSeparator>OR</StyledOrSeparator>
          <div className="share-link-header">
            <p style={{ marginBottom: "10px", color: "#675feb" }}>
              Share this link with your team to create a user account and join.
            </p>
            <CustomTooltip
              title={"This invite link will expire after one hour."}
            >
              <img src={infoIcon} alt="infoIcon" />
            </CustomTooltip>
          </div>
          <StyledLinkContainer>
            <div className="copy-field">
              <TextInput
                readOnly={true}
                value={inviteLink}
                containerStyle={{ width: "100%" }}
              />
              <div className="copy-icons-container">
                <img
                  src={copyIcon}
                  alt="copyIcon"
                  className="copy-icon"
                  onClick={handleCopyLink}
                />
              </div>
            </div>
          </StyledLinkContainer>
        </div>
      </ContentDialog>
      <ContentDialog
        isOpen={isRoleModalOpen}
        title={`Update Role for ${selectedUserName}`}
        onClose={handleCloseRoleModal}
        onCloseIconClose={handleCloseRoleModal}
        submitButtonLabel="Update Role"
        cancelButtonLabel="Cancel"
        event={handleUpdateMemberRole}
      >
        <div className="member-role-change-popup">
          <SelectInput
            placeholder="Select Job Type"
            options={updateRole}
            name="role"
            value={formik?.values?.role || ""}
            onBlur={formik?.handleBlur}
            error={formik?.touched?.role && formik?.errors?.role}
            errorMessage={formik?.errors?.role}
            onChange={formik?.handleChange}
            wrapperStyle={{
              display: "flex",
              width: "100%",
              marginBottom: "15px",
              marginTop: "-15px",
            }}
            labelStyle={{
              fontSize: "16px",
              fontWeight: 600,
              width: "fit-content",
              marginBottom: "12px",
            }}
          />
        </div>
      </ContentDialog>
      <ConfirmationPopup
        isOpen={isDeleteModalOpen}
        onClose={handleCloseMemberDeleteModal}
        onConfirm={handleRemoveMember}
        title={`Remove ${selectedUserName}`}
        subTitle="Confirmation required"
        message={"Are you sure you want to remove this user?"}
        confirmLabel="Yes, Remove"
        cancelLabel="No, Cancel"
      />
    </>
  );
}

export default Members;
